import type { ComponentType } from "react";
import {
  Icon20SparkleOutline,
  Icon28LikeOutline,
  Icon20FireAltOutline,
} from "@linqx/icons/web";

/**
 * The screen rendered inside the phone mockup for a given action.
 * Each key maps to a component in {@link ./flow-screens}.
 */
export type ScreenKey =
  | "vibes"
  | "nearby"
  | "profile"
  | "swipe"
  | "meet"
  | "match"
  | "chat"
  | "events"
  | "checkin"
  | "leaderboard";

/** A single in-app action demonstrated by one phone "shot". */
export interface FlowAction {
  /** i18n key for the short label shown in the narrative rail. */
  labelKey: string;
  /** Which phone screen to render for this action. */
  screen: ScreenKey;
}

/** A product feature, made of 3-4 sequential in-app actions. */
export interface FlowFeature {
  key: string;
  /** i18n key for the eyebrow, e.g. "01 — Discover". */
  eyebrowKey: string;
  /** i18n key for the feature title. */
  titleKey: string;
  /** i18n key for the feature description. */
  descriptionKey: string;
  icon: ComponentType<{ className?: string }>;
  /** Hex used for the soft halo behind the phone while this feature is active. */
  glow: string;
  actions: FlowAction[];
}

/**
 * The three core features of Linqx, each broken into the 3-4 main user
 * actions that the scroll animation steps through inside the phone.
 */
export const FEATURES: FlowFeature[] = [
  {
    key: "discover",
    eyebrowKey: "landing_flow_discover_eyebrow",
    titleKey: "landing_flow_discover_title",
    descriptionKey: "landing_flow_discover_desc",
    icon: Icon20SparkleOutline,
    glow: "#C47979",
    actions: [
      { labelKey: "landing_flow_discover_action_vibe", screen: "vibes" },
      { labelKey: "landing_flow_discover_action_nearby", screen: "nearby" },
      { labelKey: "landing_flow_discover_action_profile", screen: "profile" },
    ],
  },
  {
    key: "connect",
    eyebrowKey: "landing_flow_connect_eyebrow",
    titleKey: "landing_flow_connect_title",
    descriptionKey: "landing_flow_connect_desc",
    icon: Icon28LikeOutline,
    glow: "#A65555",
    actions: [
      { labelKey: "landing_flow_connect_action_swipe", screen: "swipe" },
      { labelKey: "landing_flow_connect_action_meet", screen: "meet" },
      { labelKey: "landing_flow_connect_action_match", screen: "match" },
      { labelKey: "landing_flow_connect_action_chat", screen: "chat" },
    ],
  },
  {
    key: "live",
    eyebrowKey: "landing_flow_live_eyebrow",
    titleKey: "landing_flow_live_title",
    descriptionKey: "landing_flow_live_desc",
    icon: Icon20FireAltOutline,
    glow: "#6B3434",
    actions: [
      { labelKey: "landing_flow_live_action_events", screen: "events" },
      { labelKey: "landing_flow_live_action_checkin", screen: "checkin" },
      {
        labelKey: "landing_flow_live_action_leaderboard",
        screen: "leaderboard",
      },
    ],
  },
];

/** A flattened, globally-indexed "shot" — one per action across all features. */
export interface FlowStep {
  globalIndex: number;
  featureIndex: number;
  actionIndex: number;
  feature: FlowFeature;
  action: FlowAction;
}

/** All actions flattened into an ordered list of scroll-driven shots. */
export const STEPS: FlowStep[] = (() => {
  const steps: FlowStep[] = [];
  FEATURES.forEach((feature, featureIndex) => {
    feature.actions.forEach((action, actionIndex) => {
      steps.push({
        globalIndex: steps.length,
        featureIndex,
        actionIndex,
        feature,
        action,
      });
    });
  });
  return steps;
})();

export const TOTAL_STEPS = STEPS.length;

/** Global step index of the first action of each feature (for rail math). */
export const FEATURE_START_INDEX: number[] = (() => {
  const starts: number[] = [];
  let cursor = 0;
  FEATURES.forEach((feature) => {
    starts.push(cursor);
    cursor += feature.actions.length;
  });
  return starts;
})();
